<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div
    class="dropdown b-dropdown"
    :class="dropdownClasses"
  >
    <!-- toggle -->
    <BButton
      v-if="!split"
      :id="_id"
      ref="toggle"
      class="dropdown-toggle"
      :disabled="disabled"
      :class="toggleClasses"
      aria-haspopup="true"
      :aria-expanded="visible.toString()"
      :variant="variant"
      :size="size"
      @click="toggle"
      @keydown="toggle"
    >
      <slot name="button-content">
        {{ text }}
      </slot>
    </BButton>
    <BButton
      v-if="split"
      ref="toggle"
      class="dropdown-content-split"
      :disabled="disabled"
      :variant="variant"
      :size="size"
      @click="onSplitClick"
      @keydown="onSplitClick"
    >
      <slot name="button-content">
        {{ text }}
      </slot>
    </BButton>
    <BButton
      v-if="split"
      :id="_id"
      class="dropdown-toggle"
      :disabled="disabled"
      :class="toggleClasses"
      aria-haspopup="true"
      :aria-expanded="visible.toString()"
      :variant="variant"
      :size="size"
      type="button"
      @click="toggle"
      @keydown="toggle"
    >
      <span class="sr-only">Toggle dropdown</span>
    </BButton>
    <!-- menu -->
    <ul
      ref="menu"
      class="dropdown-menu"
      :class="menuClasses"
      role="menu"
      :aria-labelledby="_id"
      tabindex="-1"
      @keydown="onMenuKeydown"
    >
      <slot />
    </ul>
  </div>
</template>

<script>
import BButton from './BButton.vue'
import Popper from 'popper.js'
import { useListenOnRoot } from './useListenOnRoot'
const ROOT_ACTION_EVENT_SHOW = 'global-open-mz-dropdown'

// Popper placement options
const PLACEMENT_TOP_START = 'top-start'
const PLACEMENT_TOP_END = 'top-end'
const PLACEMENT_BOTTOM_START = 'bottom-start'
const PLACEMENT_BOTTOM_END = 'bottom-end'
const PLACEMENT_RIGHT_START = 'right-start'
const PLACEMENT_LEFT_START = 'left-start'

// Key codes
const CODE_DOWN = 40
const CODE_ESC = 27
const CODE_UP = 38

const SELECTOR_ITEM = ['.dropdown-item', '.b-dropdown-form']
  .map(selector => `${selector}:not(.disabled):not([disabled])`)
  .join(', ')

export default {
  name: 'BDropdown',
  ariaHasPopupRoles: ['menu', 'listbox', 'tree', 'grid', 'dialog'],
  components: {
    BButton
  },
  provide() {
    return { getDropdownParent: () => this }
  },
  props: {
    right: Boolean,
    dropup: Boolean,
    dropright: Boolean,
    dropleft: Boolean,
    noCaret: Boolean,
    block: Boolean,
    split: Boolean,
    boundary: {
      type: [HTMLElement, String],
      default: 'scrollParent'
    },
    size: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'secondary'
    },
    toggleClass: {
      type: [String, Array, Object],
      default: null
    },
    menuClass: {
      type: [String, Array, Object],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: null
    }
  },
  emits: ['click', 'show', 'hide', 'toggle'],
  setup() {
    const { listenOnRoot, emitOnRoot } = useListenOnRoot()
    return {
      listenOnRoot,
      emitOnRoot
    }
  },
  data() {
    return {
      visible: false
    }
  },
  computed: {
    toggleClasses() {
      return [
        this.toggleClass,
        {
          'dropdown-toggle-split': this.split,
          'dropdown-toggle-no-caret': this.noCaret
        }
      ]
    },
    toggler() {
      const { toggle } = this.$refs
      return toggle ? toggle.$el || toggle : null
    },
    boundaryClass() {
      // Position `static` is needed to allow menu to "breakout" of the `scrollParent`
      // boundaries when boundary is anything other than `scrollParent`
      // See: https://github.com/twbs/bootstrap/issues/24251#issuecomment-341413786
      return this.boundary !== 'scrollParent' ? 'position-static' : ''
    },
    directionClass() {
      switch (true) {
        case this.dropup:
          return 'dropup'
        case this.dropright:
          return 'dropright'
        case this.dropleft:
          return 'dropleft'
        default:
          return ''
      }
    },
    dropdownClasses() {
      return [
        this.directionClass,
        this.boundaryClass,
        {
          show: this.visible,
          'btn-group': this.split || !this.block,
          'd-flex': this.split && this.block
        }
      ]
    },
    menuClasses() {
      return [
        this.menuClass,
        {
          'dropdown-menu-right': this.right,
          show: this.visible
        }
      ]
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.showMenu()
        document.body.addEventListener('click', this.clickOutsideHandler)
      } else {
        this.hideMenu()
        document.body.removeEventListener('click', this.clickOutsideHandler)
      }
    }
  },
  mounted() {
    this.listenOnRoot(ROOT_ACTION_EVENT_SHOW, this.rootEvent)
  },
  created() {
    this._id = `_BV_toggle_${parseInt(Math.random() * 200)}`
    this.$_popper = null
  },
  beforeUnmount() {
    this.visible = false
    this.destroyPopper()
  },
  methods: {
    /** POPPER  */
    updatePopper() {
      try {
        this.$_popper.scheduleUpdate()
      } catch {}
    },
    createPopper(element) {
      this.$_popper = new Popper(element, this.$refs.menu, this.getPopperConfig())
    },
    destroyPopper() {
      if (this.$_popper) {
        this.$_popper.destroy()
      }
      this.$_popper = null
    },
    getPopperConfig() {
      let placement = PLACEMENT_BOTTOM_START
      if (this.dropup) {
        placement = this.right ? PLACEMENT_TOP_END : PLACEMENT_TOP_START
      } else if (this.dropright) {
        placement = PLACEMENT_RIGHT_START
      } else if (this.dropleft) {
        placement = PLACEMENT_LEFT_START
      } else if (this.right) {
        placement = PLACEMENT_BOTTOM_END
      }
      const popperConfig = {
        placement,
        modifiers: {
          offset: { offset: this.offset || 0 },
          flip: { enabled: !this.noFlip }
        }
      }
      const boundariesElement = this.boundary
      if (boundariesElement) {
        popperConfig.modifiers.preventOverflow = { boundariesElement }
      }
      return popperConfig
    },
    /** SHOW/HIDE */
    showMenu() {
      if (this.disabled) return
      let el = (this.dropup && this.right) || this.$refs.toggle
      el = el.$el || el
      this.createPopper(el)

      this.$nextTick(() => {
        this.$refs.menu.focus()
      })
    },
    hideMenu() {
      this.destroyPopper()
    },
    toggle(e) {
      if (this.disabled) return
      this.$emit('toggle', this.visible)
      e.preventDefault()
      e.stopPropagation()
      if (!this.visible) {
        this.show()
        this.emitOnRoot(ROOT_ACTION_EVENT_SHOW, { _id: this._id })
      } else {
        this.hide()
      }
    },
    onSplitClick(event) {
      if (this.disabled) {
        this.visible = false
        return
      }
      this.$emit('click', event)
    },
    show() {
      if (this.disabled) return
      this.visible = true
      this.$emit('show')
    },
    hide() {
      if (this.disabled) return
      this.visible = false
      this.$emit('hide')
    },
    rootEvent(e) {
      if (e.detail._id !== this._id) {
        this.hide()
      }
    },
    /** EXTERNAL CONTROLS */
    clickOutsideHandler(e) {
      if (this.$el.contains(e.target)) return
      this.hide()
    },
    onMenuKeydown(e) {
      if (!this.visible) return
      const { keyCode } = e
      switch (keyCode) {
        case CODE_ESC:
          e.preventDefault()
          e.stopPropagation()
          this.hide()
          break
        case CODE_DOWN:
          this.focusNext(e, false)
          break
        case CODE_UP:
          this.focusNext(e, true)
          break
        default:
          break
      }
    },
    focusNext(e, up) {
      const { target } = e
      if (!this.visible) return
      e.preventDefault()
      e.stopPropagation()
      this.$nextTick(() => {
        const items = this.$refs.menu.querySelectorAll(SELECTOR_ITEM)
        if (items.length < 1) return
        let index = Array.from(items).indexOf(target)
        index = (up && index > 0) ? index - 1 : (!up && index < items.length - 1) ? index + 1 : 0
        items[index]?.focus()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dropdown-toggle[disabled] {
  cursor: not-allowed;
}
</style>
